import {
	callGetManaged,
	callGetManagerHome,
	callListManaged,
	residenceGetMailboxTestStatus
} from '@repo/sdk/gen'
import { queryOptions } from '@tanstack/react-query'
import { makeQueryKeys } from '@/query/query-keys'

/** Every residence of the manager with the state of its call, those to act on first. */
export function managedCallsQueryOptions() {
	return queryOptions({
		queryKey: makeQueryKeys.call.list(),
		queryFn: async ({ signal }) => (await callListManaged({ signal })).data
	})
}

/**
 * The call of one residence of the manager: dates, rules, data and checks.
 * What the page shows decides what the manager confirms and publishes, and the
 * general admin changes it from another place: it is read again when the page
 * is opened and when the window comes back into focus, never trusted for minutes.
 */
export function managedCallQueryOptions(residenceId: string, callId?: string) {
	return queryOptions({
		queryKey: makeQueryKeys.call.managed(residenceId, callId),
		queryFn: async ({ signal }) =>
			(
				await callGetManaged({
					body: { data: { residenceId, callId } },
					signal
				})
			).data,
		staleTime: 0,
		refetchOnWindowFocus: true
	})
}

/**
 * The test email of the PEC of a residence of the manager. While the mailbox
 * has not answered yet, it is read again every few seconds.
 */
export function managedMailboxTestQueryOptions(
	residenceId: string,
	callId?: string
) {
	return queryOptions({
		queryKey: makeQueryKeys.call.mailboxTest(residenceId, callId),
		queryFn: async ({ signal }) =>
			(
				await residenceGetMailboxTestStatus({
					body: { filters: { residenceId, callId } },
					signal
				})
			).data,
		staleTime: 0,
		refetchOnWindowFocus: true,
		refetchInterval: (query) =>
			query.state.data?.delivery === 'sending' ? 5000 : false
	})
}

/** The first page of the manager for one residence: call state, counters and what waits. */
export function managerHomeQueryOptions(residenceId: string, callId?: string) {
	return queryOptions({
		queryKey: makeQueryKeys.call.home(residenceId, callId),
		queryFn: async ({ signal }) =>
			(
				await callGetManagerHome({
					body: { data: { residenceId, callId } },
					signal
				})
			).data,
		staleTime: 0,
		refetchOnWindowFocus: true
	})
}
